import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckOutlined, PlayCircleOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, Card, Col, List, Row, Skeleton, Typography } from 'antd';

import { useAuth, useDemoList } from 'Providers';
import { TagsLoadingError, TeamsLoadingError } from '../../errors/DemoListErrors';
import { DemoSettings } from '../../types';
import { filterTeams, isAllTeamsFiltered } from './filterTeams';

import './styles.scss';

export function DemoList(): JSX.Element {
  const { demoList, errors } = useDemoList();
  const [filteredDemoList, setFilteredDemoList] = useState<DemoSettings[]>([]);
  const { dashboardSdk } = useAuth();
  useEffect(() => {
    async function filterDemoList() {
      const filteredLists: DemoSettings[] = demoList.isLoading ? [] : await filterTeams(demoList.data, dashboardSdk);

      setFilteredDemoList(filteredLists);
    }
    filterDemoList();
  }, [demoList.data, dashboardSdk]);

  const teamsLoadingError = errors?.find((error) => error instanceof TeamsLoadingError);
  const tagsLoadingError = errors?.find((error) => error instanceof TagsLoadingError);
  const showDemoFlowIdErrIfTagErr = !(filteredDemoList.every((e) => !isAllTeamsFiltered(e)) && tagsLoadingError);

  return (
    <div className="demo-list">
      <Typography.Title level={2}>Welcome to Bringg Playground!</Typography.Title>
      <Typography.Paragraph>
        Here you can browse Bringg product demos that simulate the system in different scenarios, market verticals and
        use-cases.
        <br />
        You can just hit play and the demo will run with default settings, or edit the demo’s settings before playing it
      </Typography.Paragraph>
      {(teamsLoadingError || tagsLoadingError) &&
        errors?.map((error, index) => (
          <p key={index}>
            <span className="demo-list__header" style={{ color: 'red' }}>
              {error.message}
            </span>
          </p>
        ))}
      {!teamsLoadingError &&
        showDemoFlowIdErrIfTagErr &&
        filteredDemoList.map((settings: DemoSettings, index) => {
          if (isAllTeamsFiltered(settings)) {
            return (
              <span key={index} className="demo-list__header" style={{ color: 'red' }}>
                <p>{settings.name}: no teams available (*no demo flow id, or users are not in team)</p>
              </span>
            );
          }
        })}
      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        {!teamsLoadingError &&
          showDemoFlowIdErrIfTagErr &&
          filteredDemoList.map((settings: DemoSettings, index) => {
            if (!isAllTeamsFiltered(settings) && !tagsLoadingError) {
              return (
                <Col key={index} sm={8} xs={12}>
                  <Card
                    title={<Typography.Title level={5}>{settings.name}</Typography.Title>}
                    extra={
                      <Link to={`/settings/${index}`}>
                        <Button className="settings-button" title="Settings" size="large" icon={<SettingOutlined />} />
                      </Link>
                    }
                    actions={[
                      <div className="card-footer">
                        <Link to={`/play/${index}`}>
                          <Button
                            title="Play"
                            size="large"
                            type="primary"
                            shape="circle"
                            icon={<PlayCircleOutlined />}
                          />
                        </Link>
                      </div>,
                    ]}
                    className="demo-list-card"
                  >
                    <Skeleton loading={!!errors?.length}>
                      <Typography.Paragraph>{settings.description}</Typography.Paragraph>
                      <List
                        size="large"
                        dataSource={settings.demoIncludes}
                        renderItem={(item) => (
                          <Typography.Paragraph>
                            <CheckOutlined className="check-mark-icon" />
                            {item}
                          </Typography.Paragraph>
                        )}
                      />
                    </Skeleton>
                  </Card>
                </Col>
              );
            }
          })}
      </Row>
    </div>
  );
}
